import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaWhatsapp,
  FaCopy,
  FaDownload,
  FaEye,
  FaExpandAlt
} from 'react-icons/fa';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../../components/firebase';
import { toast } from 'react-toastify';
import { useTheme } from '../../../context/ThemeContext';

const PdfViewerModal = ({
  isOpen,
  onClose,
  currentPdf,
  filteredNotes,
  currentIndex,
  onNavigate,
  extractFileId,
  onStatsUpdate
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { isDarkMode } = useTheme();

  const c = {
    bg: 'var(--bg-main)',
    card: 'var(--bg-card)',
    surface: 'var(--bg-surface)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    accent: 'var(--accent)',
    glass: 'var(--glass)',
    glassBorder: 'var(--glass-border)'
  };

  const updateStats = async (updateData) => {
    if (!currentPdf) return null;

    const docRef = doc(db, 'pdfStats', currentPdf.id);
    try {
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        await setDoc(docRef, {
          views: 0,
          downloads: 0,
          lastViewed: null,
          lastDownloaded: null,
          ...updateData
        });
      } else {
        await updateDoc(docRef, updateData);
      }

      const updatedDoc = await getDoc(docRef);
      const updatedStats = updatedDoc.data();

      if (onStatsUpdate) {
        onStatsUpdate(currentPdf.id, updatedStats);
      }
      return updatedStats;
    } catch (error) {
      console.error('Error updating stats:', error);
      return null;
    }
  };

  const handleDownload = async () => {
    if (!currentPdf) return;
    setIsDownloading(true);
    try {
      await updateStats({
        views: increment(1),
        lastViewed: new Date()
      });
      await updateStats({
        downloads: increment(1),
        lastDownloaded: new Date()
      });

      const fileId = extractFileId(currentPdf.link);
      if (fileId) {
        const downloadUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
        window.open(downloadUrl, '_blank');
        toast.success('Download started!');
      }
    } catch (error) {
      console.error('Error handling download:', error);
      toast.error('Failed to initiate download');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen || !currentPdf) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md p-2 md:p-6"
        style={{ backgroundColor: isDarkMode ? 'rgba(3, 7, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="rounded-[2rem] w-full max-w-6xl h-[95vh] flex flex-col shadow-[0_0_100px_rgba(0,0,0,0.5)] overflow-hidden relative border"
          style={{ backgroundColor: c.card, borderColor: c.glassBorder }}
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-5 md:px-8 border-b backdrop-blur-xl" style={{ backgroundColor: c.glass, borderColor: c.glassBorder }}>
            <div className="flex-1 min-w-0 pr-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 bg-green-400/10 border border-green-400/20 text-green-400 text-[9px] font-black uppercase tracking-wider rounded">Reading Mode</span>
              </div>
              <h3 className="font-bold text-lg md:text-xl truncate leading-tight" style={{ color: c.text }}>
                {currentPdf.title}
              </h3>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                disabled={isDownloading}
                className="p-3 rounded-xl transition-all border"
                style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.muted }}
                title="Download PDF"
              >
                {isDownloading ? <div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin rounded-full"></div> : <FaDownload size={16} />}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  const websiteUrl = `${window.location.origin}${window.location.pathname}?pdf=${currentPdf.id}`;
                  const message = `Check out this PDF: ${currentPdf.title}\n\nView it here: ${websiteUrl}`;
                  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="p-3 hover:bg-green-600 rounded-xl transition-all border"
                style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.muted }}
                title="Share on WhatsApp"
              >
                <FaWhatsapp size={16} />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  const websiteUrl = `${window.location.origin}${window.location.pathname}?pdf=${currentPdf.id}`;
                  navigator.clipboard.writeText(websiteUrl);
                  toast.success('Link copied!');
                }}
                className="p-3 hover:bg-white/10 rounded-xl transition-all border"
                style={{ backgroundColor: c.surface, borderColor: c.glassBorder, color: c.muted }}
                title="Copy Link"
              >
                <FaCopy size={16} />
              </motion.button>

              <div className="h-8 w-[1px] mx-1" style={{ backgroundColor: c.glassBorder }}></div>

              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-xl transition-all border border-red-500/20"
                title="Close"
              >
                <FaTimes size={16} />
              </motion.button>
            </div>
          </div>

          {/* Main Viewer Area */}
          <div className="flex-1 overflow-hidden relative bg-black/40">
            {filteredNotes.length > 1 && (
              <>
                <motion.button
                  whileHover={{ scale: 1.1, x: -5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigate('prev');
                  }}
                  className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 backdrop-blur-xl border p-4 rounded-full shadow-2xl z-20 transition-all"
                  style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
                >
                  <FaChevronLeft size={20} />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.1, x: 5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigate('next');
                  }}
                  className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 backdrop-blur-xl border p-4 rounded-full shadow-2xl z-20 transition-all"
                  style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
                >
                  <FaChevronRight size={20} />
                </motion.button>
              </>
            )}

            <div className="h-full w-full">
              <iframe
                src={currentPdf.directPreviewLink || `https://drive.google.com/file/d/${extractFileId(currentPdf.link)}/preview`}
                width="100%"
                height="100%"
                frameBorder="0"
                title={currentPdf.title}
                className="w-full h-full"
                style={{ backgroundColor: 'var(--bg-card)' }}
                allow="autoplay"
                loading="lazy"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t flex justify-between items-center text-xs font-semibold uppercase tracking-widest transition-colors duration-300" style={{ backgroundColor: c.glass, borderColor: c.glassBorder, color: c.muted }}>
            <div className="flex items-center gap-2">
              <FaExpandAlt className="opacity-60" />
              <span>Full Screen Mode Available</span>
            </div>
            <div className="px-4 py-1.5 rounded-full border transition-colors duration-300" style={{ backgroundColor: c.surface, borderColor: c.glassBorder }}>
              {currentIndex + 1} / {filteredNotes.length} Materials
            </div>
            <div className="hidden md:flex items-center gap-2">
              <FaEye className="opacity-60" />
              <span>Powered by SnapED Cloud</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PdfViewerModal;